import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiService, Team } from '../services/api';

interface YearGroup {
  year: number;
  groups: string[];
}

const Navigation: React.FC = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredYear, setHoveredYear] = useState<number | null>(null);
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const [yearGroups, setYearGroups] = useState<YearGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const groupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Check if device is mobile with proper responsive hook
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchYearGroups = async () => {
      try {
        const teams = await apiService.getTeams();
        
        // Group teams by year and then by group
        const grouped = teams.reduce((acc: { [key: number]: Set<string> }, team) => {
          if (!acc[team.year]) {
            acc[team.year] = new Set();
          }
          acc[team.year].add(team.group);
          return acc;
        }, {});

        // Convert to array format and sort
        const yearGroupsArray = Object.entries(grouped)
          .map(([year, groups]) => ({
            year: parseInt(year),
            groups: Array.from(groups).sort()
          }))
          .sort((a, b) => b.year - a.year); // Sort years descending

        setYearGroups(yearGroupsArray);
      } catch (error) {
        console.error('Failed to fetch year groups:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchYearGroups();
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setHoveredYear(null);
      setHoveredGroup(null);
    }
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setHoveredYear(null);
    setHoveredGroup(null);
  };

  const handleYearHover = (year: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setHoveredYear(year);
    setHoveredGroup(null);
  };

  const handleYearLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setHoveredYear(null);
      setHoveredGroup(null);
    }, 200);
  };

  const handleGroupHover = (group: string) => {
    if (groupTimeoutRef.current) {
      clearTimeout(groupTimeoutRef.current);
    }
    setHoveredGroup(group);
  };

  const handleGroupLeave = () => {
    groupTimeoutRef.current = setTimeout(() => {
      setHoveredGroup(null);
    }, 200);
  };

  const handleSubmenuEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleSubmenuLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setHoveredYear(null);
      setHoveredGroup(null);
    }, 200);
  };

  const handleThirdLevelEnter = () => {
    if (groupTimeoutRef.current) {
      clearTimeout(groupTimeoutRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleThirdLevelLeave = () => {
    groupTimeoutRef.current = setTimeout(() => {
      setHoveredGroup(null);
    }, 200);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.navigation-dropdown')) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (groupTimeoutRef.current) {
        clearTimeout(groupTimeoutRef.current);
      }
    };
  }, []);

  return (
    <nav style={{
      backgroundColor: '#1976d2',
      color: 'white',
      padding: '8px 0',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 16px',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
        {/* Navigation Items - Same for Desktop and Mobile */}
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <Link 
            to="/" 
            style={{ 
              textDecoration: 'none', 
              color: 'white',
              fontWeight: location.pathname === '/' ? 'bold' : 'normal'
            }}
          >
            首頁
          </Link>
          
          {/* Summer League Multi-Level Dropdown */}
          <div className="navigation-dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={toggleDropdown}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '16px',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '4px',
                fontWeight: location.pathname.startsWith('/summer-league') ? 'bold' : 'normal',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              Summer League
              <span style={{ fontSize: '12px' }}>
                {isDropdownOpen ? '▲' : '▼'}
              </span>
            </button>
            
            {isDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                minWidth: hoveredGroup ? '280px' : hoveredYear ? '184px' : '80px',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'stretch',
                transition: 'min-width 0.2s ease'
              }}>
                {/* First Level - Years */}
                <div style={{
                  borderRight: hoveredYear ? '1px solid #eee' : 'none',
                  minWidth: '80px',
                  width: '80px',
                  flexShrink: 0
                }}>
                  {isLoading ? (
                    <div style={{ padding: '8px 12px', color: '#666', fontSize: '14px' }}>
                      Loading...
                    </div>
                  ) : yearGroups.length > 0 ? (
                    yearGroups.map((yearGroup) => (
                      <Link
                        key={yearGroup.year}
                        to={`/summer-league/${yearGroup.year}`}
                        onClick={closeDropdown}
                        style={{
                          display: 'block',
                          position: 'relative',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: hoveredYear === yearGroup.year ? '#f8f9fa' : 'transparent',
                          textDecoration: 'none',
                          lineHeight: '1.4',
                          boxSizing: 'border-box'
                        }}
                        onMouseEnter={() => handleYearHover(yearGroup.year)}
                        onMouseLeave={handleYearLeave}
                      >
                        <span style={{ color: '#333', fontSize: '14px' }}>
                          {yearGroup.year}
                        </span>
                      </Link>
                    ))
                  ) : (
                    <div style={{ padding: '8px 12px', color: '#666', fontSize: '14px' }}>
                      No data available
                    </div>
                  )}
                </div>

                {/* Second Level - Groups */}
                {hoveredYear && (
                  <div 
                    style={{
                      minWidth: '104px',
                      backgroundColor: 'white',
                      borderRight: hoveredGroup ? '1px solid #eee' : 'none',
                      flexShrink: 0
                    }}
                    onMouseEnter={handleSubmenuEnter}
                    onMouseLeave={handleSubmenuLeave}
                  >
                    {yearGroups
                      .filter(yg => yg.year === hoveredYear)
                      .map(yearGroup => (
                        <div key={yearGroup.year}>
                          <Link
                            to={`/summer-league/${yearGroup.year}/schedule`}
                            onClick={closeDropdown}
                            style={{
                              display: 'block',
                              position: 'relative',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              backgroundColor: 'transparent',
                              textDecoration: 'none',
                              lineHeight: '1.4',
                              whiteSpace: 'nowrap',
                              boxSizing: 'border-box'
                            }}
                            onMouseEnter={(e) => {
                              if (groupTimeoutRef.current) {
                                clearTimeout(groupTimeoutRef.current);
                              }
                              setHoveredGroup(null);
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              handleGroupLeave();
                            }}
                          >
                            <span style={{ color: '#3182ce', fontSize: '14px', fontWeight: 600 }}>
                              完整賽程
                            </span>
                          </Link>
                          {yearGroup.groups.map((group) => (
                            <Link
                              key={`${yearGroup.year}-${group}`}
                              to={`/summer-league/${yearGroup.year}/${group}`}
                              onClick={closeDropdown}
                              style={{
                                display: 'block',
                                position: 'relative',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                backgroundColor: hoveredGroup === group ? '#f8f9fa' : 'transparent',
                                textDecoration: 'none',
                                lineHeight: '1.4',
                                whiteSpace: 'nowrap',
                                boxSizing: 'border-box'
                              }}
                              onMouseEnter={() => handleGroupHover(group)}
                              onMouseLeave={handleGroupLeave}
                            >
                              <span style={{ color: '#333', fontSize: '14px' }}>
                                {group}
                              </span>
                            </Link>
                          ))}
                        </div>
                      ))}
                  </div>
                )}

                {/* Third Level - Options */}
                {hoveredGroup && (
                  <div 
                    style={{
                      minWidth: '100px',
                      backgroundColor: 'white'
                    }}
                    onMouseEnter={handleThirdLevelEnter}
                    onMouseLeave={handleThirdLevelLeave}
                  >
                    <Link
                      to={`/summer-league/${hoveredYear}/${hoveredGroup}/schedule`}
                      onClick={closeDropdown}
                      style={{
                        display: 'block',
                        padding: '8px 12px',
                        textDecoration: 'none',
                        color: '#333',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      賽程
                    </Link>
                    <Link
                      to={`/summer-league/${hoveredYear}/${hoveredGroup}/teams`}
                      onClick={closeDropdown}
                      style={{
                        display: 'block',
                        padding: '8px 12px',
                        textDecoration: 'none',
                        color: '#333',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      球隊介紹
                    </Link>
                    <Link
                      to={`/summer-league/${hoveredYear}/${hoveredGroup}/awards`}
                      onClick={closeDropdown}
                      style={{
                        display: 'block',
                        padding: '8px 12px',
                        textDecoration: 'none',
                        color: '#333',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      獎項
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <Link 
            to="/highlights" 
            style={{ 
              textDecoration: 'none', 
              color: 'white',
              fontWeight: location.pathname === '/highlights' ? 'bold' : 'normal'
            }}
          >
            賽事精華
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;